// Checkout.js
import React, { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OrderSummary from '../components/OrderSummary';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/apiConfig';
import { calculateShippingAmount, useShippingSettings } from '../hooks/useShippingSettings';

const SESSION_CART_KEY = 'ccc_guest_cart';
const SESSION_SIZE_KEY = 'ccc_guest_size';

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const parseAccessorySelections = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getGuestCart = () => safeParse(sessionStorage.getItem(SESSION_CART_KEY), []);
const setGuestCart = (items) => {
  try {
    sessionStorage.setItem(SESSION_CART_KEY, JSON.stringify(items || []));
  } catch {}
  window.dispatchEvent(new Event('ccc_cart_updated'));
};

const getGuestSize = () => safeParse(sessionStorage.getItem(SESSION_SIZE_KEY), null);

const Checkout = () => {
  const [user, setUser] = useState(() => safeParse(localStorage.getItem('user'), null));
  const token = localStorage.getItem('token');
  const isLoggedIn = !!user?.userId && !!token;

  const [guestSize, setGuestSize] = useState(() => getGuestSize());

  // Address / contact (works for BOTH guest + logged-in)
  const [fullName, setFullName] = useState(user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '');
  const [address1, setAddress1] = useState(user?.address || '');
  const [zipCode, setZipCode] = useState(user?.postalCode || '');
  const [cityState, setCityState] = useState(`${user?.city || ''}${user?.state ? `, ${user.state}` : ''}`);
  const [country, setCountry] = useState(user?.country || 'United States');
  const [phone, setPhone] = useState(user?.phoneNumber || '');

  const [cartItems, setCartItems] = useState([]);
  const [step, setStep] = useState('initial');
  const [paymentMethod, setPaymentMethod] = useState('credit');

  const [orderId, setOrderId] = useState(null);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');
  const shippingSettings = useShippingSettings();

  // ✅ Coupon states
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null); // server response
  const [couponError, setCouponError] = useState('');

  const normalizeCart = (arr) => {
    const items = Array.isArray(arr) ? arr : [];

    return items.map((x) => {
      const cartId = x.cartId ?? x.cartItemId ?? x.cartItemID ?? x.id ?? null;

      const fabricImageUrl =
        x.fabricImageUrl ??
        x.fabricImageURL ??
        x.fabricImage ??
        x.fabricImagePath ??
        x.firstFabricImage ??
        x.imageUrl ??
        x.imageURL ??
        null;

      const productName = x.productName ?? x.ProductName ?? '—';
      const fabricName = x.fabricName ?? x.FabricName ?? '';

      const styleOptionNames =
        Array.isArray(x.styleOptionNames) ? x.styleOptionNames :
        Array.isArray(x.styleOptions) ? x.styleOptions :
        typeof x.styleOptions === 'string'
          ? x.styleOptions.split(',').map((s) => s.trim()).filter(Boolean)
          : [];
      const accessorySelections = parseAccessorySelections(
        x.accessorySelectionsJson ?? x.accessorySelections ?? x.AccessorySelectionsJson ?? null
      );

      const createdAt =
        x.createdAt ??
        x.created_at ??
        x.addedOn ??
        x.added_on ??
        Date.now();

      const price = Number(x.price ?? x.totalPrice ?? x.total_price ?? 0);

      const guestCartId = x.guestCartId ?? x.id ?? cartId ?? null;

      return {
        ...x,
        cartId,
        fabricImageUrl,
        productName,
        fabricName,
        styleOptionNames,
        accessorySelections,
        createdAt,
        price,

        guestCartId: guestCartId ?? `g-${Date.now()}-${Math.random()}`,
        _id: cartId ?? guestCartId ?? `g-${Date.now()}-${Math.random()}`
      };
    });
  };

  const loadCart = async () => {
    if (isLoggedIn) {
      try {
        const res = await fetch(`${API_BASE_URL}/UserManagement/GetCartByUserId/${user.userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (!res.ok) {
          const text = await res.text();
          console.error('GetCartByUserId failed:', res.status, text);
          setCartItems([]);
          return;
        }

        const data = await res.json();
        setCartItems(normalizeCart(data));
      } catch (err) {
        console.error('Failed to fetch cart:', err);
        setCartItems([]);
      }
      return;
    }

    // guest
    setGuestSize(getGuestSize());
    setCartItems(normalizeCart(getGuestCart()));
  };

  useEffect(() => {
    const syncUser = () => {
      const u = safeParse(localStorage.getItem('user'), null);
      setUser(u);

      // refresh form fields from user on login
      if (u) {
        setFullName(`${u.firstName || ''} ${u.lastName || ''}`.trim());
        setAddress1(u.address || '');
        setZipCode(u.postalCode || '');
        setCityState(`${u.city || ''}${u.state ? `, ${u.state}` : ''}`);
        setCountry(u.country || 'United States');
        setPhone(u.phoneNumber || '');
      }
    };

    const syncCart = () => loadCart();
    const syncGuestSize = () => setGuestSize(getGuestSize());

    window.addEventListener('storage', syncUser);
    window.addEventListener('ccc_user_updated', syncUser);

    window.addEventListener('ccc_cart_updated', syncCart);
    window.addEventListener('ccc_guest_size_updated', syncGuestSize);

    loadCart();

    return () => {
      window.removeEventListener('storage', syncUser);
      window.removeEventListener('ccc_user_updated', syncUser);
      window.removeEventListener('ccc_cart_updated', syncCart);
      window.removeEventListener('ccc_guest_size_updated', syncGuestSize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user?.userId]);

  const calculateSubtotal = () => cartItems.reduce((total, item) => total + Number(item.price || 0), 0);

  // ✅ stable subtotal + shipping
  const subtotal = useMemo(() => calculateSubtotal(), [cartItems]);
  const shipping = useMemo(
    () => calculateShippingAmount(subtotal, shippingSettings),
    [subtotal, shippingSettings]
  );

  // ✅ if cart changes, coupon result can become invalid (min order, etc.) -> clear it
  useEffect(() => {
    if (couponResult) {
      setCouponResult(null);
      setCouponError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, shipping]);

  // ✅ total after discount (used for Order create)
  const totalAmount = useMemo(() => {
    if (couponResult?.success && typeof couponResult.totalAfter !== 'undefined') {
      return Number(couponResult.totalAfter || 0);
    }
    if (couponResult?.success && typeof couponResult.totalAfterDiscount !== 'undefined') {
      return Number(couponResult.totalAfterDiscount || 0);
    }
    if (couponResult?.success && typeof couponResult.totalAfter === 'string') {
      return Number(couponResult.totalAfter);
    }
    if (couponResult?.success && typeof couponResult.totalAfterDiscount === 'string') {
      return Number(couponResult.totalAfterDiscount);
    }

    // fallback: compute locally
    const discountAmt = Number(couponResult?.discountAmount || 0);
    const newSubtotal = Math.max(0, subtotal - discountAmt);
    return Number(newSubtotal + shipping);
  }, [couponResult, subtotal, shipping]);

  const selectPayment = (method) => setPaymentMethod(method);

  const handleContinue = () => {
    if (!cartItems.length) return;
    setStep('form');
  };

  const handleBackToInitial = () => setStep('initial');
  const handleBackToForm = () => setStep('form');

  // --- SIZE HELPERS (for showing + sending in payload) ---
  const getSizeSnapshotForItem = (item) => {
    return item.sizeSnapshot || item.guestSizeSnapshot || guestSize || null;
  };

  const renderSizeLabel = (item) => {
    // server fields
    if (item.smartSizeName) return `Smart Size: ${item.smartSizeName}`;
    if (item.bodyMeasurementLabel) return 'Body Measurement';
    if (item.shirtMeasurementLabel) return 'Shirt Measurement';
    if (item.isMailUsSelected) return 'Mail Us a Shirt';

    // guest (session)
    const gs = getSizeSnapshotForItem(item);
    if (!gs) return '—';

    const gsType = String(gs.sizeType || '').toLowerCase();

    if (gsType.includes('smart')) return `Smart Size: ${gs.smartSizeName || gs.smartSize || 'Selected'}`;
    if (gsType.includes('body')) return 'Body Measurement';
    if (gsType.includes('shirt')) return 'Shirt Measurement';
    if (gsType.includes('mail')) return 'Mail Us a Shirt';

    if (gsType.includes('standard') || !gsType) {
      const s = gs.standard || {};
      const collar = s.collarSize ?? s.collar ?? '';
      const sleeve = s.sleeveLength ?? s.sleeve ?? '';
      const fit = s.fit ?? '';
      const parts = [];
      if (collar) parts.push(`Collar ${collar}`);
      if (sleeve) parts.push(`Sleeve ${sleeve}`);
      if (fit) parts.push(fit);
      return parts.length ? `Standard Size: ${parts.join(', ')}` : 'Standard Size';
    }

    return '—';
  };

  const buildSizePayload = (item) => {
    const bodyMeasurementFromItem = item.bodyMeasurement || item.BodyMeasurement || null;
    const shirtMeasurementFromItem = item.shirtMeasurement || item.ShirtMeasurement || null;

    const normalizeBodyMeasurement = (m) => {
      if (!m) return null;
      return {
        neckAround: m.neckAround ?? m.NeckAround ?? null,
        sleeveLength: m.sleeveLength ?? m.SleeveLength ?? null,
        shoulderWidth: m.shoulderWidth ?? m.ShoulderWidth ?? null,
        chestAround: m.chestAround ?? m.ChestAround ?? null,
        waistAround: m.waistAround ?? m.WaistAround ?? null,
        shirtLength: m.shirtLength ?? m.ShirtLength ?? null,
        bicepAround: m.bicepAround ?? m.BicepAround ?? null,
        wristAround: m.wristAround ?? m.WristAround ?? null,
        fit: m.fit ?? m.Fit ?? null
      };
    };

    const normalizeShirtMeasurement = (m) => {
      if (!m) return null;
      return {
        collarAround: m.collarAround ?? m.CollarAround ?? null,
        sleeveLength: m.sleeveLength ?? m.SleeveLength ?? null,
        yokeWidth: m.yokeWidth ?? m.YokeWidth ?? null,
        chestWidth: m.chestWidth ?? m.ChestWidth ?? null,
        midsectionWidth: m.midsectionWidth ?? m.MidsectionWidth ?? null,
        shirtLength: m.shirtLength ?? m.ShirtLength ?? null,
        sleeveWidth: m.sleeveWidth ?? m.SleeveWidth ?? null,
        cuffAround: m.cuffAround ?? m.CuffAround ?? null
      };
    };

    // Logged-in order item fields
    const standardCollar =
      item.standardCollarSize ??
      item.standardCollar ??
      item.collarSize ??
      null;
    const standardSleeve =
      item.standardSleeveLength ??
      item.standardSleeve ??
      item.sleeveLength ??
      null;
    const standardFit =
      item.standardFit ??
      item.fit ??
      null;
    const hasStandardSize =
      standardCollar !== null ||
      standardSleeve !== null ||
      (typeof standardFit === 'string' && standardFit.trim() !== '');

    if (item.smartSizeName || item.bodyMeasurementLabel || item.shirtMeasurementLabel || item.isMailUsSelected || hasStandardSize) {
      return {
        sizeType: item.smartSizeName ? 'smart' :
          item.bodyMeasurementLabel ? 'body' :
          item.shirtMeasurementLabel ? 'shirt' :
          item.isMailUsSelected ? 'mail' :
          hasStandardSize ? 'standard' : '',
        smartSizeName: item.smartSizeName || '',
        bodyMeasurementLabel: item.bodyMeasurementLabel || '',
        shirtMeasurementLabel: item.shirtMeasurementLabel || '',
        bodyMeasurementId: item.bodyMeasurementId ?? item.BodyMeasurementId ?? null,
        shirtMeasurementId: item.shirtMeasurementId ?? item.ShirtMeasurementId ?? null,
        bodyMeasurement: normalizeBodyMeasurement(bodyMeasurementFromItem),
        shirtMeasurement: normalizeShirtMeasurement(shirtMeasurementFromItem),
        isMailUsSelected: !!item.isMailUsSelected,
        standard: hasStandardSize ? {
          collarSize: standardCollar ?? '',
          sleeveLength: standardSleeve ?? '',
          fit: standardFit ?? ''
        } : undefined
      };
    }

    // Guest snapshot
    const gs = getSizeSnapshotForItem(item);
    if (!gs) return { sizeType: '' };

    const t = String(gs.sizeType || '').toLowerCase();

    if (t.includes('smart')) return { sizeType: 'smart', smartSizeName: gs.smartSizeName || gs.smartSize || '' };
    if (t.includes('body')) return { sizeType: 'body', bodyMeasurement: normalizeBodyMeasurement(gs.body || gs.bodyMeasurement || null) };
    if (t.includes('shirt')) return { sizeType: 'shirt', shirtMeasurement: normalizeShirtMeasurement(gs.shirt || gs.shirtMeasurement || null) };
    if (t.includes('mail')) return { sizeType: 'mail' };

    return { sizeType: 'standard', standard: gs.standard || {} };
  };

  // ✅ Apply coupon via public API
  const applyCoupon = async () => {
    setCouponError('');
    setCouponResult(null);

    const code = (couponCode || '').trim();
    if (!code) {
      setCouponError('Enter a coupon code.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/discounts/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          couponCode: code,
          subtotal: Number(subtotal),
          shipping: Number(shipping)
        })
      });

      const data = await res.json().catch(() => null);

      if (!data) {
        setCouponError('Invalid response from server.');
        return;
      }

      if (!data.success) {
        setCouponError(data.message || 'Coupon not valid.');
        return;
      }

      setCouponResult(data);
    } catch (err) {
      console.error('Apply coupon failed:', err);
      setCouponError('Failed to apply coupon.');
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponResult(null);
    setCouponError('');
  };

  const handleFormContinue = async () => {
    if (!cartItems.length) return;

    // basic validation (both guest + logged-in)
    if (!fullName.trim() || !address1.trim() || !zipCode.trim() || !country.trim() || !phone.trim()) {
      alert('Please fill all required address fields.');
      return;
    }

    const buildStyleAndAccessoryText = (item) => {
      const styles = (item.styleOptionNames || []).filter(Boolean);
      const accessory = (item.accessorySelections || [])
        .map((s) => {
          const left = s?.label || s?.key || 'Option';
          const right = s?.optionLabel || s?.value || '';
          return right ? `${left}: ${right}` : left;
        })
        .filter(Boolean);

      if (styles.length && accessory.length) return `${styles.join(', ')} | Accessory: ${accessory.join('; ')}`;
      if (styles.length) return styles.join(', ');
      if (accessory.length) return `Accessory: ${accessory.join('; ')}`;
      return '';
    };

    const cartPayload = cartItems.map((item) => ({
      productId: item.productId ?? item.productID ?? item.product_id ?? 0,
      fabricId: item.fabricId ?? item.fabricID ?? item.fabric_id ?? 0,
      productName: item.productName || '',
      fabricName: item.fabricName || '',
      styleOptions: buildStyleAndAccessoryText(item),
      price: Number(item.price || 0),

      // size
      ...buildSizePayload(item),

      // monogram
      isMonogram: item.isMonogram || false,
      monogramInitials: item.monogramInitials || '',
      monogramStyle: item.monogramStyle || '',
      monogramColor: item.monogramColor || '',
      monogramPlacement: item.monogramPlacement || ''
    }));

    const city = (cityState.split(',')[0] || '').trim();
    const state = (cityState.split(',')[1] || '').trim();

    // ✅ supports BOTH: if guest, userId is null and isGuestOrder=true
    const orderData = {
      userId: isLoggedIn ? user.userId : null,
      isGuestOrder: !isLoggedIn,

      // contact/shipping
      fullName: fullName.trim(),
      shippingAddress: address1,
      city,
      state,
      postalCode: zipCode,
      country,
      phoneNumber: phone,

      // ✅ totals with coupon applied
      subtotalAmount: Number(subtotal),
      shippingAmount: Number(shipping),
      discountCode: couponResult?.couponCode || couponCode?.trim() || null,
      discountAmount: Number(couponResult?.discountAmount || 0),
      totalAmount: Number(totalAmount),

      cartItems: cartPayload
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isLoggedIn && token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && result.orderId) {
        setOrderId(result.orderId);
        setStep('payment');
      } else {
        console.error('Create order failed:', response.status, result);
        alert(result?.message || 'Failed to create order. Please try again.');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error creating order.');
    }
  };

  const handleProcessPayment = async () => {
    if (!orderId) {
      setPaymentMessage('Order not created yet.');
      return;
    }

    setLoading(true);
    setPaymentMessage('');

    const paymentData = {
      orderId,
      cardNumber,
      expiry: expiry.replace('/', ''),
      cvv,
      cardholderName,

      // ✅ pass discount info if your backend wants it here too (safe if ignored)
      discountCode: couponResult?.couponCode || couponCode?.trim() || null,
      discountAmount: Number(couponResult?.discountAmount || 0)
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/process-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isLoggedIn && token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(paymentData)
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && result.success) {
        setPaymentMessage('✅ Payment Successful!');
        setCartItems([]);

        // ✅ clear correct cart storage
        if (isLoggedIn) {
          window.dispatchEvent(new Event('ccc_cart_updated'));
        } else {
          setGuestCart([]);
        }

        const trackingNumber = result.trackingNumber;

        if (!trackingNumber) {
          window.location.href = `/track-order`;
          return;
        }

        window.location.href = `/track-order?trackingNumber=${encodeURIComponent(trackingNumber)}`;
      } else {
        setPaymentMessage(result.message || 'Payment failed.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentMessage('Error processing payment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main className="rbt-main-wrapper">

        {/* STEP 1: Cart Overview */}
        {step === 'initial' && (
          <section className="checkout-user">
            <div className="custom-checkout-container">
              <div className="row">
                <div className="col-lg-8">
                  <div className="custom-checkout-box">
                    <h2 className="custom-checkout-title">Checkout</h2>

                    {!isLoggedIn && (
                      <div className="mb-3" style={{ color: '#666' }}>
                        You’re checking out as a guest (saved in this browser). You can still log in anytime.
                        <div className="mt-2">
                          <Link to="/login" className="rbt-btn btn-gradient btn-sm">Login</Link>
                        </div>
                      </div>
                    )}

                    <div className="checkout-products-wrapper">
                      <h2 className="checkout-products-heading">Garments To Be Sent First</h2>
                      <p className="checkout-products-note">
                        If it’s your first time ordering a custom product, we strongly encourage you to select one garment to be sent first.
                      </p>

                      <div className="checkout-products-steps">
                        <div className="checkout-products-step">
                          <span className="checkout-products-step-number">1</span>
                          <div className="steps-heading">
                            <strong>Select garments to be made first.</strong>
                            <p>We'll make them for you and keep the other items on hold.</p>
                          </div>
                        </div>
                        <div className="checkout-products-step">
                          <span className="checkout-products-step-number">2</span>
                          <div className="steps-heading">
                            <strong>Upon receipt, confirm your fit.</strong>
                            <p>Make necessary changes to on-hold items before they are cut and sewn.</p>
                          </div>
                        </div>
                        <div className="checkout-products-step">
                          <span className="checkout-products-step-number">3</span>
                          <div className="steps-heading">
                            <strong>On-hold garments will be started as soon as you request them.</strong>
                            <p>If necessary, we’ll remake your first garment in an adjusted size.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="checkout-products-list">
                      <h3 className="checkout-products-subtitle">
                        Cart Items <span>({cartItems.length} Items)</span>
                      </h3>

                      {cartItems.map((item, index) => (
                        <div className="checkout-products-item" key={item._id || index}>
                          <div className="switch-img">
                            <img
                              src={item.fabricImageUrl || "/assets/images/productImg1.jpg"}
                              alt={item.productName}
                              className="checkout-products-image"
                            />
                          </div>
                          <div className="checkout-products-details">
                            <p className="checkout-products-name">{item.productName}</p>
                            <p className="checkout-products-status">{item.fabricName}</p>

                            {item.styleOptionNames?.length > 0 && (
                              <p className="checkout-products-status">
                                Styles: {item.styleOptionNames.join(', ')}
                              </p>
                            )}

                            <p className="checkout-products-status">
                              Size: {renderSizeLabel(item)}
                            </p>

                            <p className="checkout-products-status">
                              <strong>${Number(item.price || 0).toFixed(2)}</strong>
                            </p>
                          </div>
                        </div>
                      ))}

                      {cartItems.length === 0 && (
                        <p className="text-muted">Your cart is empty.</p>
                      )}
                    </div>

                    {cartItems.length > 0 && (
                      <div className="d-flex justify-content-between mt-4">
                        <button onClick={() => window.history.back()} className="rbt-btn btn-gradient hover-icon-reverse">
                          <span className="icon-reverse-wrapper">
                            <span className="btn-icon" style={{ transform: 'translateX(-10px)' }}><i className="feather-arrow-left"></i></span>&nbsp;&nbsp;&nbsp;
                            <span className="btn-text">Back</span>
                          </span>
                        </button>
                        <button onClick={handleContinue} className="rbt-btn btn-gradient hover-icon-reverse">
                          <span className="icon-reverse-wrapper">
                            <span className="btn-text">Continue</span>
                            <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* ✅ Pass discount info (safe if OrderSummary ignores extras) */}
                <OrderSummary
                  subtotal={subtotal}
                  shipping={shipping}
                  shippingLabel={shippingSettings.shippingLabel}
                  freeShippingMessage={shippingSettings.freeShippingMessage}
                  discountAmount={Number(couponResult?.discountAmount || 0)}
                  total={Number(totalAmount)}
                  couponCode={couponResult?.couponCode || ''}

                  // ✅ coupon input + actions (OrderSummary UI uses these)
                  couponInput={couponCode}
                  onCouponInputChange={setCouponCode}
                  onApplyCoupon={applyCoupon}
                  onRemoveCoupon={removeCoupon}
                  couponError={couponError}
                  isCouponApplied={!!couponResult?.success}
                />

              </div>
            </div>
          </section>
        )}

        {/* STEP 2: Address Form (guest + logged-in) */}
        {step === 'form' && (
          <section className="checkout-user">
            <div className="custom-checkout-container">
              <div className="row">
                <div className="col-lg-8">
                  <div className="checkout-form-section">
                    <form onSubmit={(e) => { e.preventDefault(); handleFormContinue(); }}>
                      <label>Full Name</label>
                      <input
                        type="text"
                        placeholder="Enter full name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                      />

                      <label>Address</label>
                      <input
                        type="text"
                        placeholder="Address"
                        value={address1}
                        onChange={(e) => setAddress1(e.target.value)}
                      />

                      <div className="checkout-form-row">
                        <input
                          type="text"
                          placeholder="ZIP"
                          value={zipCode}
                          onChange={(e) => setZipCode(e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="City, State"
                          value={cityState}
                          onChange={(e) => setCityState(e.target.value)}
                        />
                      </div>

                      <div className="checkout-form-row">
                        <select value={country} onChange={(e) => setCountry(e.target.value)}>
                          <option>United States</option>
                          <option>Canada</option>
                          <option>India</option>
                        </select>

                        <input
                          type="text"
                          placeholder="Phone"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>

                      <div className="form-checkbox-group">
                        <label className="custom-checkbox">
                          <input type="checkbox" className="styled-checkbox" />
                          This address is a P.O. Box
                        </label><br />
                        <label className="custom-checkbox">
                          <input type="checkbox" className="styled-checkbox" />
                          Subscribe to receive Text/SMS messages
                        </label>
                      </div>

                      {/* ✅ Coupon UI */}
                      {/* <div className="mt-4">
                        <label>Coupon Code</label>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <input
                            type="text"
                            placeholder="Enter coupon code"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            style={{ flex: 1 }}
                          />
                          {!couponResult?.success ? (
                            <button type="button" className="checkout-submit-btn" onClick={applyCoupon}>
                              Apply
                            </button>
                          ) : (
                            <button type="button" className="checkout-submit-btn" onClick={removeCoupon}>
                              Remove
                            </button>
                          )}
                        </div>

                        {couponError && <div className="mt-2" style={{ color: 'red' }}>{couponError}</div>}

                        {couponResult?.success && (
                          <div className="mt-2" style={{ color: 'green' }}>
                            Applied {couponResult.couponCode}: -${Number(couponResult.discountAmount || 0).toFixed(2)}
                          </div>
                        )}
                      </div> */}

                      {!isLoggedIn && (
                        <div className="mt-3" style={{ color: '#666' }}>
                          Optional: <Link to="/login">Login</Link> to save your details for next time.
                        </div>
                      )}

                      <div className="d-flex justify-content-between mt-4">
                        <button type="button" onClick={handleBackToInitial} className="checkout-submit-btn">Back</button>
                        <button type="button" onClick={handleFormContinue} className="checkout-submit-btn">Continue</button>
                      </div>
                    </form>
                  </div>
                </div>

                <OrderSummary
                  subtotal={subtotal}
                  shipping={shipping}
                  shippingLabel={shippingSettings.shippingLabel}
                  freeShippingMessage={shippingSettings.freeShippingMessage}
                  discountAmount={Number(couponResult?.discountAmount || 0)}
                  total={Number(totalAmount)}
                  couponCode={couponResult?.couponCode || ''}

                  // ✅ coupon input + actions (OrderSummary UI uses these)
                  couponInput={couponCode}
                  onCouponInputChange={setCouponCode}
                  onApplyCoupon={applyCoupon}
                  onRemoveCoupon={removeCoupon}
                  couponError={couponError}
                  isCouponApplied={!!couponResult?.success}
                />

              </div>
            </div>
          </section>
        )}

        {/* STEP 3: Payment */}
        {step === 'payment' && (
          <section className="checkout-user">
            <div className="custom-checkout-container">
              <div className="row">
                <div className="col-lg-8">
                  <h3>PAYMENT</h3>
                  <p>All transactions are secure and encrypted.</p>

                  <div className="payment-methods">
                    <button className={paymentMethod === 'credit' ? 'selected' : ''} onClick={() => selectPayment('credit')}>Credit Card</button>
                  </div>

                  {paymentMethod === 'credit' ? (
                    <div>
                      <input type="text" placeholder="Card Number" value={cardNumber} onChange={e => setCardNumber(e.target.value)} /><br />
                      <input type="text" placeholder="MM / YY" value={expiry} onChange={e => setExpiry(e.target.value)} /><br />
                      <input type="text" placeholder="CVC" value={cvv} onChange={e => setCvv(e.target.value)} /><br />
                      <input type="text" placeholder="Name on Card" value={cardholderName} onChange={e => setCardholderName(e.target.value)} /><br />
                      <br />
                      <label className="custom-checkbox">
                        <input type="checkbox" className="styled-checkbox" defaultChecked />
                        Securely store my payment details
                      </label>
                    </div>
                  ) : (
                    <div>
                      <p>We will redirect you to PayPal.</p>
                    </div>
                  )}

                  <div className="d-flex justify-content-between mt-4">
                    <button onClick={handleBackToForm} className="form-btn">Back</button>
                    <button onClick={handleProcessPayment} className="form-btn" disabled={loading}>
                      {loading ? 'Processing...' : 'Continue'}
                    </button>
                  </div>

                  {paymentMessage && (
                    <div className="mt-3" style={{ color: paymentMessage.includes('Success') ? 'green' : 'red' }}>
                      {paymentMessage}
                    </div>
                  )}
                </div>

                <OrderSummary
                  subtotal={subtotal}
                  shipping={shipping}
                  shippingLabel={shippingSettings.shippingLabel}
                  freeShippingMessage={shippingSettings.freeShippingMessage}
                  discountAmount={Number(couponResult?.discountAmount || 0)}
                  total={Number(totalAmount)}
                  couponCode={couponResult?.couponCode || ''}

                  // ✅ coupon input + actions (OrderSummary UI uses these)
                  couponInput={couponCode}
                  onCouponInputChange={setCouponCode}
                  onApplyCoupon={applyCoupon}
                  onRemoveCoupon={removeCoupon}
                  couponError={couponError}
                  isCouponApplied={!!couponResult?.success}
                />

              </div>
            </div>
          </section>
        )}

      </main>
      <Footer />
    </>
  );
};

export default Checkout;
